import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import NameCard from "@/components/NameCard";
import MeritCard from "@/components/MeritCard";
import FengShuiCompass from "@/components/FengShuiCompass";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ScrollText, Compass, Star, Sparkles, TrendingUp, CloudSun, Gift, CalendarRange, Download, Loader2,
  Palette, Home, BedDouble, Shirt, User, Award, Briefcase, Coins, Heart, HeartHandshake, Activity, Users, MapPin,
} from "lucide-react";

const WUXING_COLOR = {
  金: "hsl(45 65% 42%)", 木: "hsl(150 42% 36%)", 水: "hsl(210 55% 45%)",
  火: "hsl(6 70% 48%)", 土: "hsl(30 46% 40%)",
};
const el = (ch) => WUXING_COLOR[ch] || "hsl(var(--brand-ink))";

// Feng-shui chip: coloured swatch dot + label
function DirChip({ swatch, label, muted }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
        muted ? "border-dashed border-border bg-[hsl(var(--secondary))]/40 text-muted-foreground" : "border-border bg-card text-foreground"
      }`}
    >
      {swatch && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: swatch }} />}
      {label}
    </span>
  );
}

// Grouped feng-shui field (label + chips)
function FsField({ icon: Icon, label, children, testid }) {
  return (
    <div className="rounded-lg bg-[hsl(var(--secondary))]/40 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-2">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

// Feng-shui narrative note (icon + label + paragraph)
function FsNote({ icon: Icon, label, text, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <p className="text-sm leading-6 text-foreground/85">{text}</p>
    </div>
  );
}

export default function ChartView() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [view, setView] = useState("ganzhi"); // ganzhi | shishen | palace | flow
  const [selDayun, setSelDayun] = useState(null); // index of expanded da-yun
  const [selYear, setSelYear] = useState(null);
  const [yearData, setYearData] = useState(null);
  const [yearLoading, setYearLoading] = useState(false);

  const load = useCallback(() => {
    setState("loading");
    setSelDayun(null); setSelYear(null); setYearData(null);
    api
      .get("/chart", { params: { lang } })
      .then((r) => {
        if (r.data.profile_complete === false) { setData(r.data); setState("incomplete"); }
        else { setData(r.data); setState("ready"); }
      })
      .catch(() => setState("error"));
  }, [lang]);
  useEffect(() => { load(); }, [load]);

  const loadYear = useCallback((yr) => {
    if (!yr) return;
    setSelYear(String(yr));
    setYearLoading(true);
    setYearData(null);
    api.get("/chart/year", { params: { year: yr, lang } })
      .then((r) => setYearData(r.data.available ? r.data : { error: true }))
      .catch(() => setYearData({ error: true }))
      .finally(() => setYearLoading(false));
  }, [lang]);

  const downloadPdf = useCallback(async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/chart/pdf", { params: { lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `CiAO-Merit-${(user?.name || "chart")}-${lang}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === "zh" ? "命盘 PDF 已生成" : "Chart PDF ready");
    } catch (e) {
      toast.error(lang === "zh" ? "生成失败，请稍后再试" : "Failed to generate, please try again");
    } finally {
      setPdfBusy(false);
    }
  }, [lang, user]);

  if (state === "loading") return (
    <div className="space-y-3" data-testid="chart-loading">
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-[hsl(var(--brand-gold-soft)/0.18)] py-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-bronze))]" />
        <div className="font-display text-lg font-semibold text-[hsl(var(--brand-ink))]">
          {lang === "zh" ? "命盘计算中…" : "Calculating your chart…"}
        </div>
        <p className="text-sm text-muted-foreground max-w-sm">
          {lang === "zh" ? "首次生成约需 30–45 秒，正在推演四柱、五行与大运，请稍候。" : "First-time generation takes ~30–45s while we compute your Four Pillars, Five Elements and luck cycles."}
        </p>
      </div>
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-56 w-full rounded-xl" />
    </div>
  );
  if (state === "incomplete") return <Card className="border-border"><CardContent className="p-6 text-center text-muted-foreground">{lang === "zh" ? data?.message_zh : data?.message_en}</CardContent></Card>;
  if (state === "error") return <Card className="border-border"><CardContent className="p-6 text-center"><p className="text-muted-foreground">{t("faq.error")}</p><Button className="mt-3" onClick={load}>{t("faq.retry")}</Button></CardContent></Card>;

  const c = data.chart; const a = data.analysis || {};
  const pk = ["year", "month", "day", "time"];
  const pillarLabel = { year: t("chart.year"), month: t("chart.month"), day: t("chart.day"), time: t("chart.time") };

  const views = [
    ["ganzhi", t("chart.viewGanzhi")],
    ["shishen", t("chart.viewShiShen")],
    ["palace", t("chart.viewPalace")],
    ["flow", t("chart.viewFlow")],
  ];

  const renderPillarCell = (k) => {
    const pp = c.pillars[k];
    const gEl = (pp.wuXing || "  ")[0];
    const zEl = (pp.wuXing || "  ")[1];
    if (view === "ganzhi") {
      return (
        <div className="text-center">
          <div className="font-display text-2xl font-bold leading-none" style={{ color: el(gEl) }}>{pp.gan}</div>
          <div className="font-display text-2xl font-bold leading-none mt-1" style={{ color: el(zEl) }}>{pp.zhi}</div>
          <div className="mt-2 text-[11px] text-muted-foreground space-y-0.5">
            <div>{t("chart.hideGan")}: {(pp.hideGan || []).join(" ")}</div>
            <div>{t("chart.naYin")}: {pp.naYin}</div>
            <div>{t("chart.diShi")}: {pp.diShi}</div>
            <div>{t("chart.xunKong")}: {pp.xunKong}</div>
          </div>
        </div>
      );
    }
    if (view === "shishen") {
      return (
        <div className="text-center">
          <div className="font-display text-lg font-bold text-[hsl(var(--brand-ink))]">{pp.ganZhi}</div>
          <div className="mt-2 text-xs">
            <div><span className="text-muted-foreground">{t("chart.ganShiShen")}:</span> <span className="font-semibold text-[hsl(var(--brand-vermillion))]">{pp.shiShenGan}</span></div>
            <div className="mt-1"><span className="text-muted-foreground">{t("chart.zhiShiShen")}:</span> {(pp.shiShenZhi || []).join(" / ")}</div>
          </div>
          <div className="mt-2 text-[11px] text-[hsl(var(--brand-bronze))]">{lang === "zh" ? pp.relative_zh : pp.relative_en}</div>
        </div>
      );
    }
    if (view === "palace") {
      return (
        <div className="text-center">
          <div className="font-display text-lg font-bold text-[hsl(var(--brand-ink))]">{pp.ganZhi}</div>
          <div className="mt-2 text-xs text-[hsl(var(--brand-bronze))] font-semibold">{lang === "zh" ? pp.palace_zh : pp.palace_en}</div>
          <div className="mt-1 text-[11px] text-muted-foreground">{t("chart.diShi")}: {pp.diShi}</div>
        </div>
      );
    }
    // flow
    return (
      <div className="text-center">
        <div className="font-display text-lg font-bold text-[hsl(var(--brand-ink))]">{pp.ganZhi}</div>
        <div className="mt-2 flex justify-center gap-1">
          {(pp.wuXing || "").split("").map((ch, i) => (
            <span key={i} className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-white" style={{ background: el(ch) }}>{ch}</span>
          ))}
        </div>
      </div>
    );
  };

  const wuxing = c.wuxing_counts || {};
  const maxWx = Math.max(1, ...Object.values(wuxing));
  const dayunList = c.dayun?.list || [];
  const allYears = Array.from(new Set(dayunList.flatMap((d) => (d.liunian || []).map((l) => l.year)))).sort((x, y) => x - y);
  const thisYear = new Date().getFullYear();

  // Feng-shui base + in-depth reading items (Phase 15)
  const fs = c.fengshui || {};
  const fsDir = (arr) => (arr || []).map((d) => ({ swatch: d.swatch, label: lang === "zh" ? d.zh : d.en }));
  const hasFengshui = (fs.auspicious_directions?.length || 0) > 0 || a.fengshui_summary || a.bed_desk || a.home_layout || a.wear_advice;
  const deepItems = [
    { key: "personality", icon: User, label: t("chart.personality"), body: a.personality },
    { key: "talents", icon: Award, label: t("chart.talents"), body: a.talents },
    { key: "career", icon: Briefcase, label: t("chart.career"), body: a.career },
    { key: "wealth", icon: Coins, label: t("chart.wealth"), body: [a.wealth, a.wealth_types].filter(Boolean).join("\n\n") },
    { key: "relationship", icon: Heart, label: t("chart.relationship"), body: a.relationship },
    { key: "marriage", icon: HeartHandshake, label: t("chart.marriage"), body: a.marriage },
    { key: "health", icon: Activity, label: t("chart.health"), body: a.health },
    { key: "relatives", icon: Users, label: t("chart.relatives"), body: a.relatives },
  ].filter((x) => x.body);

  return (
    <div className="space-y-5" data-testid="chart-view">
      {/* PDF export */}
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          className="border-[hsl(var(--brand-bronze))] text-[hsl(var(--brand-bronze))] hover:bg-[hsl(var(--brand-gold-soft))]/40"
          onClick={downloadPdf}
          disabled={pdfBusy}
          data-testid="chart-download-pdf"
        >
          {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
          {t("chart.exportPdf")}
        </Button>
      </div>

      {/* Summary + name card */}
      <div className="grid md:grid-cols-2 gap-5 items-start">
        <Card className="border-border bg-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><ScrollText className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h2 className="font-display text-xl font-semibold">{t("chart.title")}</h2></div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("card.dayMaster")}</span><span className="font-semibold">{c.day_master}（{c.day_master_wuxing}）</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("card.zodiac")}</span><span className="font-semibold">{c.zodiac}</span></div>
              <div className="flex justify-between col-span-2"><span className="text-muted-foreground">{t("chart.lunar")}</span><span>{c.lunar_date}</span></div>
              <div className="flex justify-between col-span-2"><span className="text-muted-foreground">{t("chart.solar")}</span><span className="font-mono-num text-xs">{c.solar}</span></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline">{t("chart.mingGong")} {c.mingGong}</Badge>
              <Badge variant="outline">{t("chart.shenGong")} {c.shenGong}</Badge>
              <Badge variant="outline">{t("chart.taiYuan")} {c.taiYuan}</Badge>
            </div>
          </CardContent>
        </Card>

        <NameCard name={user?.name} chart={c} overall={a.overall} />
      </div>

      {/* Four pillars multi-view */}
      <Card className="border-border bg-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h3 className="font-display text-lg font-semibold">{t("chart.pillarsTitle")}</h3></div>
            <div className="flex flex-wrap gap-1 rounded-lg bg-[hsl(var(--secondary))] p-1">
              {views.map(([v, label]) => (
                <button key={v} onClick={() => setView(v)} data-testid={`pillar-view-${v}`}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${view === v ? "bg-[hsl(var(--primary))] text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {pk.map((k) => (
              <div key={k} className="rounded-xl border border-border bg-[hsl(var(--secondary))]/40 p-3" data-testid={`pillar-${k}`}>
                <div className="text-center text-[11px] font-semibold text-[hsl(var(--brand-bronze))] mb-2">{pillarLabel[k]}</div>
                {renderPillarCell(k)}
              </div>
            ))}
          </div>
          {view === "flow" && (
            <div className="mt-4">
              <div className="text-xs text-muted-foreground mb-2">{t("chart.wuxingDist")}</div>
              <div className="space-y-1.5">
                {["金", "木", "水", "火", "土"].map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-5 text-sm font-semibold" style={{ color: el(k) }}>{k}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div className="h-full" style={{ width: `${(wuxing[k] || 0) / maxWx * 100}%`, background: el(k) }} />
                    </div>
                    <span className="w-6 text-right font-mono-num text-sm">{wuxing[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Xiu + SiXiang */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Card className="border-border bg-card" data-testid="chart-xiu">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><Star className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h3 className="font-display text-lg font-semibold">{t("chart.xiuTitle")}</h3></div>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-3xl font-bold text-[hsl(var(--brand-ink))]">{c.xiu.name}</span>
              <Badge className={c.xiu.luck === "吉" ? "bg-[hsl(150_42%_36%)] text-white" : "bg-[hsl(var(--brand-vermillion))] text-white"}>{c.xiu.luck}</Badge>
            </div>
            <div className="mt-2 text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
              <span>{t("chart.zheng")}: {c.xiu.zheng}</span>
              <span>{t("chart.animal")}: {c.xiu.animal}</span>
              <span>{t("chart.gong")}: {c.xiu.gong}</span>
            </div>
            {a.xiu_reading && <p className="mt-3 text-sm leading-6 text-foreground/85">{a.xiu_reading}</p>}
          </CardContent>
        </Card>

        <Card className="border-border bg-card" data-testid="chart-sixiang">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3"><Compass className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h3 className="font-display text-lg font-semibold">{t("chart.sixiangTitle")}</h3></div>
            <div className="flex items-baseline gap-3">
              <span className="font-display text-2xl font-bold text-[hsl(var(--brand-ink))]">{lang === "zh" ? c.sixiang.symbol_zh : c.sixiang.symbol_en}</span>
              <Badge variant="outline">{lang === "zh" ? c.sixiang.dir_zh : c.sixiang.dir_en}</Badge>
            </div>
            {a.sixiang_reading && <p className="mt-3 text-sm leading-6 text-foreground/85">{a.sixiang_reading}</p>}
          </CardContent>
        </Card>
      </div>

      {/* Day pillar qi + in-depth reading */}
      {(a.day_pillar_qi || a.reading || deepItems.length > 0) && (
        <Card className="border-border bg-card" data-testid="chart-deep"><CardContent className="p-5 space-y-4">
          {a.day_pillar_qi && (
            <div>
              <div className="flex items-center gap-2 mb-1"><CloudSun className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("chart.dayQi")}</span></div>
              <p className="text-sm leading-6 text-foreground/85">{a.day_pillar_qi}</p>
            </div>
          )}
          {a.reading && (
            <div>
              <div className="flex items-center gap-2 mb-1"><ScrollText className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("chart.reading")}</span></div>
              <p className="text-sm leading-7 text-foreground/85">{a.reading}</p>
            </div>
          )}
          {deepItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between flex-wrap gap-1 mb-1">
                <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--brand-bronze))]" /><span className="font-semibold">{t("chart.deepTitle")}</span></div>
                <span className="text-[11px] text-muted-foreground">{t("chart.deepDesc")}</span>
              </div>
              <Accordion type="single" collapsible defaultValue={deepItems[0]?.key} className="w-full" data-testid="chart-deep-accordion">
                {deepItems.map((it) => (
                  <AccordionItem key={it.key} value={it.key} data-testid={`chart-deep-${it.key}`}>
                    <AccordionTrigger className="text-sm hover:no-underline py-3">
                      <span className="flex items-center gap-2"><it.icon className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />{it.label}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-7 text-foreground/85 whitespace-pre-line">{it.body}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
          {(a.advice || []).length > 0 && (
            <div className="rounded-lg bg-[hsl(var(--brand-gold-soft))]/40 p-3">
              <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1">{t("chart.advice")}</div>
              <ul className="list-disc pl-4 space-y-1 text-sm">{a.advice.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Personal feng-shui directions (Phase 15) */}
      {hasFengshui && (
        <Card className="border-border bg-card" data-testid="chart-fengshui"><CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1"><Compass className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h3 className="font-display text-lg font-semibold">{t("chart.fengshuiTitle")}</h3></div>
          <p className="text-xs text-muted-foreground mb-4">{t("chart.fengshuiDesc")}</p>
          {a.fengshui_summary && <p className="text-sm leading-7 text-foreground/85 mb-4">{a.fengshui_summary}</p>}
          <div className="grid md:grid-cols-2 gap-5 items-center">
            <div>
              <FengShuiCompass favorable={fs.favorable_elements} reduce={fs.reduce_elements} lang={lang} />
              <div className="flex items-center justify-center gap-4 mt-1 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--brand-gold))]" />{t("chart.fsFavorable")}</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border border-dashed border-[hsl(var(--muted-foreground))]" />{t("chart.fsAvoid")}</span>
              </div>
            </div>
            <div className="space-y-3">
              {fsDir(fs.auspicious_directions).length > 0 && (
                <FsField icon={Compass} label={t("chart.fsFavorable")} testid="fengshui-favorable">
                  {fsDir(fs.auspicious_directions).map((d, i) => <DirChip key={i} swatch={d.swatch} label={d.label} />)}
                </FsField>
              )}
              {fsDir(fs.avoid_directions).length > 0 && (
                <FsField icon={MapPin} label={t("chart.fsAvoid")} testid="fengshui-avoid">
                  {fsDir(fs.avoid_directions).map((d, i) => <DirChip key={i} swatch={d.swatch} label={d.label} muted />)}
                </FsField>
              )}
            </div>
          </div>
          {(fsDir(fs.lucky_colors).length > 0 || (fs.lucky_items || []).length > 0) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {fsDir(fs.lucky_colors).length > 0 && (
                <FsField icon={Palette} label={t("chart.fsColors")} testid="fengshui-colors">
                  {fsDir(fs.lucky_colors).map((d, i) => <DirChip key={i} swatch={d.swatch} label={d.label} />)}
                </FsField>
              )}
              {(fs.lucky_items || []).length > 0 && (
                <FsField icon={Sparkles} label={t("chart.fsItems")} testid="fengshui-items">
                  {(fs.lucky_items || []).map((d, i) => <DirChip key={i} label={lang === "zh" ? d.zh : d.en} />)}
                </FsField>
              )}
            </div>
          )}
          {(a.bed_desk || a.home_layout || a.wear_advice) && (
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              {a.bed_desk && <FsNote icon={BedDouble} label={t("chart.fsBedDesk")} text={a.bed_desk} testid="fengshui-bed-desk" />}
              {a.home_layout && <FsNote icon={Home} label={t("chart.fsHome")} text={a.home_layout} testid="fengshui-home" />}
              {a.wear_advice && <FsNote icon={Shirt} label={t("chart.fsWear")} text={a.wear_advice} testid="fengshui-wear" />}
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Merit recommendations (based on weak/lacking elements) */}
      {(data.recommendations?.length > 0 || a.merit_recommendation) && (
        <Card className="border-border bg-card" data-testid="chart-recommendations">
          <CardContent className="p-5">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <Gift className="h-5 w-5 text-[hsl(var(--brand-bronze))]" />
              <h3 className="font-display text-lg font-semibold">{t("chartx.recTitle")}</h3>
              {(data.needed_labels || []).map((n) => (
                <Badge key={n.code} className="bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))]" data-testid={`need-element-${n.code}`}>
                  {t("chartx.needLabel")} · {lang === "zh" ? n.zh : n.en}
                </Badge>
              ))}
            </div>
            {a.merit_recommendation && <p className="text-sm leading-6 text-foreground/85 mb-3">{a.merit_recommendation}</p>}
            {data.recommendations?.length > 0 && (
              <>
                <div className="text-xs text-muted-foreground mb-3">{t("chartx.recDesc")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="chart-rec-items">
                  {data.recommendations.map((p) => <MeritCard key={p.id} project={p} />)}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* DaYun + LiuNian + LiuYue (selectable) */}
      <Card className="border-border bg-card"><CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-[hsl(var(--brand-bronze))]" /><h3 className="font-display text-lg font-semibold">{t("chart.dayunTitle")}</h3></div>
          {allYears.length > 0 && (
            <div className="flex items-center gap-1.5">
              <CalendarRange className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />
              <Select value={selYear || undefined} onValueChange={(v) => loadYear(v)}>
                <SelectTrigger className="h-8 w-[128px]" data-testid="chart-year-select"><SelectValue placeholder={t("chartx.selectYear")} /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {allYears.map((y) => <SelectItem key={y} value={String(y)}>{y}{lang === "zh" ? "年" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {c.dayun?.start && <div className="text-xs text-muted-foreground mb-1">{t("chart.startYun")}: {c.dayun.start}</div>}
        <div className="text-[11px] text-muted-foreground mb-2">{t("chartx.pickDayun")}</div>
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="chart-dayun">
          {dayunList.map((d, i) => (
            <button key={i} onClick={() => setSelDayun(selDayun === i ? null : i)} data-testid={`dayun-${i}`}
              className={`shrink-0 w-20 rounded-lg border p-2 text-center transition-colors ${selDayun === i ? "border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft))]/60" : d.current ? "border-[hsl(var(--brand-vermillion))] bg-[hsl(var(--brand-vermillion))]/10" : "border-border bg-[hsl(var(--secondary))]/40 hover:border-[hsl(var(--brand-gold))]"}`}>
              <div className="font-display font-bold text-[hsl(var(--brand-ink))]">{d.ganZhi}</div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono-num">{d.startYear}</div>
              <div className="text-[10px] text-muted-foreground font-mono-num">{d.startAge}{lang === "zh" ? "岁" : "y"}</div>
              {d.current && <div className="text-[9px] text-[hsl(var(--brand-vermillion))] font-semibold mt-0.5">{t("chart.current")}</div>}
            </button>
          ))}
        </div>

        {selDayun != null && dayunList[selDayun] && (
          <div className="mt-3" data-testid="chart-dayun-liunian">
            <div className="text-xs text-muted-foreground mb-2">{t("chartx.dayunYears")} · {dayunList[selDayun].ganZhi}</div>
            <div className="flex flex-wrap gap-2">
              {(dayunList[selDayun].liunian || []).map((l) => (
                <button key={l.year} onClick={() => loadYear(l.year)} data-testid={`liunian-${l.year}`}
                  className={`rounded-md px-2 py-1 text-xs font-mono-num transition-colors ${String(l.year) === selYear ? "bg-[hsl(var(--primary))] text-primary-foreground" : l.year === thisYear ? "bg-[hsl(var(--brand-vermillion))]/15 text-[hsl(var(--brand-vermillion))]" : "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--brand-gold-soft))]"}`}>
                  {l.year} {l.ganZhi}
                </button>
              ))}
            </div>
          </div>
        )}

        {(yearLoading || yearData) && (
          <div className="mt-4 rounded-xl border border-border bg-[hsl(var(--secondary))]/30 p-4" data-testid="chart-year-detail">
            {yearLoading ? (
              <div className="space-y-2"><div className="text-sm text-muted-foreground">{t("chartx.loadingYear")}</div><Skeleton className="h-24 w-full rounded-lg" /></div>
            ) : yearData?.error ? (
              <div className="text-sm text-muted-foreground text-center py-4">{t("faq.error")}</div>
            ) : yearData?.year_data ? (
              <>
                <div className="flex items-baseline gap-2 flex-wrap mb-3">
                  <span className="font-display text-2xl font-bold text-[hsl(var(--brand-ink))]" data-testid="year-detail-year">{yearData.year_data.year}</span>
                  <Badge variant="outline" className="font-mono-num">{yearData.year_data.ganZhi}</Badge>
                  {yearData.year_data.dayun_ganZhi && <span className="text-xs text-muted-foreground">{t("chart.dayunTitle")}: {yearData.year_data.dayun_ganZhi}</span>}
                </div>
                <div className="text-xs text-muted-foreground mb-1">{t("chartx.liuyue")}</div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-3" data-testid="chart-liuyue">
                  {(yearData.year_data.months || []).map((m, i) => (
                    <div key={i} className="rounded-md bg-card border border-border py-1.5 text-center">
                      <div className="text-[10px] text-[hsl(var(--brand-bronze))]">{m.month_zh}{t("chartx.monthCol")}</div>
                      <div className="font-display text-sm font-bold text-[hsl(var(--brand-ink))]">{m.ganZhi}</div>
                    </div>
                  ))}
                </div>
                {yearData.analysis?.overall && (
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-[hsl(var(--brand-bronze))] mb-1">{t("chartx.yearReading")}</div>
                    <p className="text-sm leading-6 text-foreground/85">{yearData.analysis.overall}</p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-2">
                  {[["career", t("chart.career")], ["wealth", t("chart.wealth")], ["health", t("chart.health")], ["relationship", t("chart.relationship")]].map(([k, label]) => (
                    yearData.analysis?.[k] ? (
                      <div key={k} className="rounded-lg bg-card p-2.5 border border-border">
                        <div className="text-[11px] font-semibold text-[hsl(var(--brand-bronze))] mb-0.5">{label}</div>
                        <div className="text-sm leading-6 text-foreground/85">{yearData.analysis[k]}</div>
                      </div>
                    ) : null
                  ))}
                </div>
                {(yearData.analysis?.advice || []).length > 0 && (
                  <div className="mt-3 rounded-lg bg-[hsl(var(--brand-gold-soft))]/40 p-2.5">
                    <div className="text-[11px] font-semibold text-[hsl(var(--brand-bronze))] mb-0.5">{t("chartx.yearAdvice")}</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-sm">{yearData.analysis.advice.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {(yearData.analysis?.caution || []).length > 0 && (
                  <div className="mt-2 rounded-lg bg-[hsl(var(--secondary))] p-2.5">
                    <div className="text-[11px] font-semibold text-[hsl(var(--brand-bronze))] mb-0.5">{t("chartx.yearCaution")}</div>
                    <ul className="list-disc pl-4 space-y-0.5 text-sm">{yearData.analysis.caution.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        {a.dayun_reading && <p className="mt-3 text-sm leading-6 text-foreground/85">{a.dayun_reading}</p>}
      </CardContent></Card>

      <p className="text-center text-[11px] text-muted-foreground">{t("chart.disclaimer")}</p>
    </div>
  );
}
